// Screen 20 · Settings — the editable backend, and screen 21 · the change log.
// Settings answers: what does the system call things, and where are its thresholds?
// Everything here is a row: workflow steps, board states, card cap and floor, option
// labels, market ranges, thresholds, contact types, whether the feedback bar shows.
// Every change is logged with who and when.

import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { updateRow, useRows, type Row } from '../lib/api';
import { longDate } from '../lib/format';
import { useAllJobs } from '../lib/jobs';
import { useSession } from '../lib/session';
import { useStepChanges, waitingOn } from '../lib/stepchanges';
import { Btn, Edit, Empty, Head, Info, Ln, Loading, Panel, Seg, Tag } from '../ui/kit';
import { History, ValueEdit } from '../ui/settingvalue';
import { PipelineSteps } from './PipelineSteps';
import { Permissions } from './Users';
import { WorkflowSteps } from './WorkflowSteps';

export function Settings() {
  const s = useSession();
  const [params, setParams] = useSearchParams();
  const canSettings = s.can('screen.settings');
  const tab = params.get('view') ?? (canSettings ? 'settings' : s.can('screen.people') ? 'users' : 'changes');
  useCrumb([{ label: 'Settings' }]);
  // the step changes waiting on each steps tab, as a count on its name (docs/step-changes-brief.md, row PS2; 2026-10-24)
  const changes = useStepChanges();
  const pending = (view: 'pipeline' | 'workflow', label: string) => {
    const n = waitingOn(changes.rows, view).length;
    return n ? (
      <>
        {label} · <span className="schip n">{n}</span>
      </>
    ) : (
      label
    );
  };
  const tabs = [
    ...(canSettings ? [{ key: 'settings', label: 'Settings' as ReactNode }, { key: 'workflow', label: pending('workflow', 'Workflow steps') }] : []),
    ...(canSettings || s.can('see.pipeline_process') ? [{ key: 'pipeline', label: pending('pipeline', 'Pipeline steps') }] : []),
    ...(s.can('screen.people') ? [{ key: 'users', label: 'Users' }] : []),
    ...(s.can('screen.change_log') ? [{ key: 'changes', label: 'Change log' }] : []),
  ];
  return (
    <div>
      {/* the step tables open straight under the tabs, as Iris drew them (docs/screens/steps-table-screens.html) */}
      {tab !== 'pipeline' && tab !== 'workflow' && <Head title="Settings" sub="The editable backend: names, states, thresholds and labels. Nothing here is fixed in code." />}
      <div style={{ marginBottom: 12 }}>
        <Seg items={tabs} value={tab} onChange={(k) => setParams({ view: k }, { replace: true })} />
      </div>
      {tab === 'settings' && canSettings && <SettingList />}
      {tab === 'workflow' && canSettings && <WorkflowSteps />}
      {tab === 'pipeline' && (canSettings || s.can('see.pipeline_process')) && <PipelineSteps />}
      {tab === 'users' && s.can('screen.people') && <Permissions />}
      {tab === 'changes' && <ChangeLog />}
    </div>
  );
}

// ---------- settings ----------

function SettingList() {
  const { user } = useSession();
  const settings = useRows('setting', { order: ['group', 'label'] });
  const users = useRows('user');
  const [open, setOpen] = useState<string | null>(null);
  if (settings.loading) return <Loading />;
  // the Library group (catalog families) is edited where it is used: Library → Catalog families
  const groups = [...new Set(settings.rows.map((r) => (r.group as string) ?? 'Other'))].filter((g) => g !== 'Library');
  const save = (row: Row, value: unknown) => updateRow('setting', row.id, { value, updated_by: user?.id ?? null });

  return (
    <div className="row">
      {settings.rows.some((r) => r.group === 'Library') && (
        <div className="col">
          <Panel label="Library">
            <p className="muted">
              Catalog families are edited where they are used, under <Link to="/library/families">Library → Catalog families</Link>.
            </p>
          </Panel>
        </div>
      )}
      {groups.map((g) => (
        <div key={g} className="col">
          <Panel label={g}>
            {settings.rows.filter((r) => ((r.group as string) ?? 'Other') === g).map((r) => (
              <div key={r.id} className="ln" style={{ display: 'block' }}>
                <div className="row mid" style={{ justifyContent: 'space-between' }}>
                  <b>{r.label}</b>
                  <span>
                    <Info left>
                      Key: <code>{r.key}</code>. Last changed {longDate(r.updated_at)}
                      {r.updated_by ? ` by ${users.rows.find((u) => u.id === r.updated_by)?.name ?? 'someone'}` : ' (as loaded)'}.
                    </Info>{' '}
                    <button type="button" className="tag off read" onClick={() => setOpen(open === r.id ? null : r.id)}>
                      history
                    </button>
                  </span>
                </div>
                <ValueEdit value={r.value} onSave={(v) => save(r, v)} />
                {open === r.id && <History table="setting" rowId={r.id} users={users.rows} />}
              </div>
            ))}
          </Panel>
        </div>
      ))}
    </div>
  );
}


// ---------- change log ----------

function ChangeLog() {
  const log = useRows('change_log', { order: ['-created_at'] });
  const users = useRows('user');
  const all = useAllJobs();
  const [only, setOnly] = useState('open');
  if (log.loading) return <Loading />;
  const shown = log.rows.filter((r) => (only === 'blocked' ? r.kind === 'blocked' : only === 'all' || r.status !== 'done'));
  const fresh = log.rows.filter((r) => r.status === 'new').length;
  const blocked = log.rows.filter((r) => r.kind === 'blocked' && r.status !== 'done').length;
  // the reason as one short phrase under a Blocked entry (BL3, 2026-10-24); what the call carried stays on the tag's hover
  const reasonWords: Record<string, string> = { cell: 'refused by its cell on the Users grid', owner_only: 'refused: owner only', no_twin: 'refused: no connector twin yet' };
  const triedWords = (r: Row) => (r.tried && typeof r.tried === 'object' && Object.keys(r.tried as object).length ? Object.entries(r.tried as Record<string, unknown>).filter(([k]) => !['reason', 'source'].includes(k)).map(([k, v]) => `${k} ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(', ') : '');
  return (
    <Panel
      label={`Change log (${fresh} new${blocked ? `, ${blocked} blocked` : ''})`}
      right={
        <>
          <Info left>What gets typed in the bar at the bottom of every screen lands here, with the screen, the job and the device it came from. A live session works the new ones and marks them done. The bar is hidden by the "Feedback bar showing" setting; the log stays. Since 2026-10-23 a connector call that was refused lands here too, as <b>Blocked</b> under the employee's name: the tool, what it tried, and why (its cell on the Users grid, the owner-only list, or no connector twin yet).</Info>{' '}
          <Seg items={[{ key: 'open', label: 'Open' }, ...(blocked ? [{ key: 'blocked', label: `Blocked · ${blocked}` }] : []), { key: 'all', label: 'All' }]} value={only} onChange={setOnly} />
        </>
      }
    >
      {shown.length === 0 && <Empty>Nothing logged yet. Use the bar at the bottom of any screen.</Empty>}
      {shown.map((r) => {
        const j = all.jobs.find((x) => x.job.id === r.job_id);
        return (
          <Ln
            key={r.id}
            left={
              <>
                {r.kind === 'blocked' ? (
                  <>
                    <span className="tag blocked" title={triedWords(r) ? `${r.tool ?? 'a call'} · tried: ${triedWords(r)}` : String(r.tool ?? 'a call')}>Blocked</span> <b>{r.tool ?? 'a call'}</b> · {r.text}
                    <br />
                    <small className="blkwhy">{r.reason_kind === 'cell' ? <Link to="/settings?view=users">{reasonWords.cell}</Link> : r.reason_kind ? reasonWords[String(r.reason_kind)] ?? `refused: ${String(r.reason_kind)}` : 'refused'}</small>
                    <br />
                  </>
                ) : (
                  <>
                    “{r.text}”
                    <br />
                  </>
                )}
                <small>
                  {r.screen ? <Link to={r.screen}>{r.screen.split('?')[0]}</Link> : 'no screen'}
                  {j ? ` · ${j.opp?.name ?? ''}` : ''} · {r.device ?? 'device unknown'} · {users.rows.find((u) => u.id === r.by_user_id)?.name ?? 'someone'} · {longDate(r.created_at)}
                </small>
                {r.kind === 'blocked' && r.instead ? (
                  <small className="blkwhy">Instead: {String(r.instead)}</small>
                ) : null}
                <Edit className="cell" value={r.handled_note ?? ''} placeholder={r.kind === 'blocked' ? "the owner's call" : 'note from whoever handled it'} onSave={(v) => updateRow('change_log', r.id, { handled_note: v || null })} />
              </>
            }
            right={
              <span className="nowrap">
                {r.status === 'new' && <Tag kind="un">new</Tag>}{' '}
                <select value={r.status} onChange={(e) => void updateRow('change_log', r.id, { status: e.target.value })}>
                  <option value="new">new</option>
                  <option value="picked_up">picked up</option>
                  <option value="done">done</option>
                </select>
              </span>
            }
          />
        );
      })}
      {shown.length > 0 && only === 'open' && log.rows.length > shown.length && (
        <div style={{ marginTop: 8 }}>
          <Btn small onClick={() => setOnly('all')}>
            Show {log.rows.length - shown.length} done
          </Btn>
        </div>
      )}
    </Panel>
  );
}
